<template>
  <v-sheet id="ai-inspect-wrapper" class="mt-4" :class="{ 'filter-applied': filtersApplied }">
    <div class="d-flex flex-row justify-space-between align-center" style="gap: 8px;">
      <v-text-field
        class="flex-grow-1 mr-2 search-input"
        style="max-width: 360px"
        v-model="options.search"
        append-icon="mdi-magnify"
        placeholder="Search..."
        single-line
        solo
        dense
        hide-details
        clearable>
      </v-text-field>

      <div class="d-flex align-center">
        
        <v-btn
          class="mr-2 rounded-pill"
          color="error"
          solo-inverted
          :height="toolbarHeight"
          v-if="canDeleteMulti()"
          @click="deleteMulti">
          <v-icon left>mdi-delete</v-icon>
          <span class="font-weight-bold"> Delete </span>
        </v-btn>
        <v-btn
          outlined
          class="mr-2"
          color="primary"
          :height="toolbarHeight"
          @click="openFilters">
          <v-icon left>mdi-filter-outline</v-icon>
          <span class="font-weight-bold"> Filters </span>
        </v-btn>
        <v-btn
          v-if="canIsolate()"
          @click="isolateInspects"
          outlined
          color="primary"
          class="rounded-pill"
          :height="toolbarHeight">
          <span class="font-weight-bold">Isolate</span>
        </v-btn>
      </div>
    </div>
    <v-row
      class="row mt-2 py-2 mx-3 flex-nowrap overflow-auto"
      style="gap: 8px"
      v-if="filtersApplied">
      <template v-for="[key, value] in Object.entries(filters)">
        <v-sheet
          v-if="value.length > 0"
          :key="key"
          class="d-flex align-center px-4 py-1 rounded-pill"
          style="border: 1px solid var(--v-primary-base)">
          <v-icon color="primary">mdi-filter</v-icon>
          <span class="subtitle-1">{{ getFilterOptionDisplayName(key) }}</span>
          <v-chip
            v-for="option in value"
            :key="option"
            class="ml-2"
            color="primary"
            small
            label
            @click="updateFilters(key, option)"
            :style="{
              background: options.filters[key]?.includes(option)
                ? 'var(--v-primary-base)'
                : 'var(--v-colored_bg-base)',
            }">
            {{ option }}
          </v-chip>
        </v-sheet>
      </template>
    </v-row>
    <v-row class="py-3 h-100">
      <v-col :sm="viewerOpened ? 8 : 12" class="h-100">
        <v-data-table
          ref="inspectsTable"
          class="elevation-0 rounded-2 d-flex flex-column table-sticky inspects-table h-100"
          style="border: 1px solid var(--v-colored_bg-base)"
          select="multiple"
          @click:row="showCoatingAnalysisOn360"
          background-color="#F3F5F2"
          color="primary"
          :footer-props="{
            showFirstLastPage: true,
            firstIcon: 'mdi-chevron-double-left',
            lastIcon: 'mdi-chevron-double-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right',
            itemsPerPageText: 'Row/Page',
            itemsPerPageOptions: [10, 15, 30, 50, 100, 200, 300, 400, 500, -1],
          }"
          :options.sync="options"
          :headers="coatingAnalysisHeaders"
          :items="entries"
          :loading="loading"
          :server-items-length="total"
          show-expand
          :expanded.sync="expanded"
          item-key="id">
          <template v-slot:[`item.friendlyId`]="{ item }">
            <span>
              <p class="ma-0">{{ item.friendlyId }}</p>
            </span>
          </template>

          <template v-slot:[`item.anomalyCount`]="{ item }">
            <v-chip
              color="primary"
              small
              label>
              {{ item.anomalyInsights ? item.anomalyInsights.length : 0 }}
            </v-chip>
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <span>
              <p class="ma-0">{{ item.name || '-' }}</p>
            </span>
          </template>

          <template v-slot:[`item.masterTagName`]="{ item }">
            <span>
              <p v-if="item.masterTagName" class="ma-0" :title="item.masterTagName">
                {{ $truncateString(item.masterTagName, 20) }}
              </p>
              <p v-else class="ma-0">-</p>
            </span>
          </template>

          <template v-slot:[`item.title`]="{ item }">
            <span>
              <p class="ma-0" :title="item.title">
                {{ item.title ? $truncateString(item.title, 30) : '-' }}
              </p>
            </span>
          </template>

          <template v-slot:[`item.heightFromDeck`]="{ item }">
            <span>
              <p class="ma-0">{{ item.heightFromDeck || '-' }}</p>
            </span>
          </template>

          <template v-slot:[`item.schedule`]="{ item }">
            <span>
              <p class="ma-0">{{ item.schedule || '-' }}</p>
            </span>
          </template>

          <template v-slot:[`item.predictedWtLoss`]="{ item }">
            <span>
              <p class="ma-0">{{ item.predictedWtLoss || '-' }}</p>
            </span>
          </template>

          <template v-slot:[`item.severity`]="{ item }">
            <v-btn
              class="bg_force_white--text"
              :color="getSeverityColor(item.severity)"
              small>
              <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Level {{ item.severity || '-' }}</span>
            </v-btn>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  :color="getStatusBackground(item.status)"
                  :style="{ color: getStatusColor(item.status) }"
                  style="min-width: 100px"
                  class="text-center"
                  dense
                  small
                  label>
                  {{ item.status || '-' }}
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  v-for="status in statuses"
                  :key="status"
                  @click="updateStatus(item, status)">
                  <v-list-item-title>{{ status }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:[`item.priority`]="{ item }">
            <v-chip
              :color="getPriorityColor(item.priority)"
              class="text-center"
              dense
              small
              label>
              {{ item.priority || '-' }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="text-center d-flex flex-column">
              <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
              <p class="font-weight-medium">No Data Available!</p>
            </div>
          </template>

          <template v-slot:loading>
            <div class="text-center d-flex flex-column">
              <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
              <p class="font-weight-medium">Loading Inspects...</p>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-sheet color="#FAFAFA" class="pa-4">
                <h3 class="mb-3">AI Findings ({{ item.anomalyInsights ? item.anomalyInsights.length : 0 }})</h3>
                <v-data-table
                  :headers="anomalyInsightHeaders"
                  :items="item.anomalyInsights || []"
                  hide-default-footer
                  disable-pagination
                  class="elevation-0 anomaly-insights-table"
                  dense
                  @click:row="showInspectsPointsOn360">
                  <template v-slot:[`item.thumbnailUrl`]="{ item: insight }">
                    <v-list-item-avatar size="50" class="inspect-thumbnail rounded-lg ma-0">
                      <v-tooltip right :z-index="999" nudge-right="-4px">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                            class="thumbnail-wrapper"
                            style="width: 100%; height: 100%">
                            <img
                              class="pa-1"
                              style="cursor: pointer !important"
                              :src="insight.tiledImageUriPath"
                              :key="insight.tiledImageUriPath" />
                          </div>
                        </template>
                        <span>
                          <v-img max-width="200" max-height="200" :src="insight.tiledImageUriPath"></v-img>
                        </span>
                      </v-tooltip>
                    </v-list-item-avatar>
                  </template>

                  <template v-slot:[`item.friendlyId`]="{ item: insight }">
                    <span>
                      <p class="ma-0">{{ insight.friendlyId }}</p>
                    </span>
                  </template>

                  <template v-slot:[`item.category`]="{ item: insight }">
                    <v-chip
                      color="#fb8c0024"
                      style="color: #fb8c00; min-width: 100px"
                      class="text-center"
                      dense
                      small
                      label>
                      {{ insight.category || '-' }}
                    </v-chip>
                  </template>

                  <template v-slot:[`item.type`]="{ item: insight }">
                    <v-chip
                      style="min-width: 100px"
                      :color="getTypeBackground(insight.type)"
                      :style="{ color: getTypeColor(insight.type) }"
                      class="text-center"
                      dense
                      small
                      label>
                      {{ insight.type || '-' }}
                    </v-chip>
                  </template>

                  <template v-slot:[`item.severity`]="{ item: insight }">
                    <v-chip
                      :color="getSeverityBackground(insight.severity)"
                      :style="{ color: getSeverityColor(insight.severity) }"
                      class="text-center"
                      dense
                      small
                      label>
                      {{ insight.severity }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-sheet>
            </td>
          </template>
        </v-data-table>
      </v-col>
      <v-col v-show="viewerOpened" sm="4" class="viewers-section pl-0 h-100">
        <viewers-section
          ref="viewersSection"
          :viewerTab.sync="viewerTab"
          @closeViewersSection="closeInspectViewer">
        </viewers-section>
        <v-sheet
          v-if="availableScenes.length && viewerTab === '360 View'"
          color="transparent"
          height="110px"
          class="scroller d-flex align-center">
          <div class="d-flex">
            <v-list-item-avatar
              v-for="item of availableScenes"
              :key="item.id"
              size="80"
              class="inspect-thumbnail rounded-lg ma-0"
              @click="navigateToInspect(item)">
              <v-tooltip top :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="thumbnail-wrapper"
                    :style="{
                      backgroundColor: openedInspect?.id === item.id ? 'var(--v-primary-base)' : '',
                    }"
                    style="width: 100%; height: 100%">
                    <img
                      class="pa-1"
                      style="cursor: pointer !important"
                      :src="item.tiledImageUriPath"
                      :key="item.tiledImageUriPath" />
                  </div>
                </template>
                <span>
                  <v-img max-width="150" max-height="150" :src="item.tiledImageUriPath"></v-img>
                </span>
              </v-tooltip>
            </v-list-item-avatar>
          </div>
        </v-sheet>
      </v-col>
    </v-row>
    <div
      class="w-100 h-100"
      style="position: absolute; top: 0; visibility: hidden; z-index: -1"
      id="viewer3d-temp"></div>
    <InsightsFilters ref="filters"></InsightsFilters>
    <AddInspectToPlan ref="addToPlan"></AddInspectToPlan>
    <InspectReportPopup ref="inspectReport"></InspectReportPopup>
  </v-sheet>
</template>
<script>
  /* eslint-disable no-unused-vars */
  const THREE = window.THREE;

  import { $truncateString } from '@/utils';
  import coatingAnalysisHeaders from '../config/coatingAnalysis.headers';
  import { mapGetters, mapState } from 'vuex';
  import InsightsFilters from '../components/InsightsFilters.vue';
  import AddInspectToPlan from '../components/AddInspectToPlan.vue';
  import InspectReportPopup from '../components/InspectReportPopup.vue';
  import { pixelToYawPitch } from '../utils/panorama';
  import { getFilterOptionDisplayName } from '../config/helper';
  import ViewersSection from '../components/ViewersSection.vue';
  import { ForgeInspection } from '@/scripts/js/forgeModels';
  import { getTagsFrom3DPoint } from '@/utils/forgeUtils';
import aIInspectService from '../service/aIInspect.service';
import coatingAnalyzerService from '../service/coatingAnalyzer.service';
import { log, warn, error } from '@/utils/debugLogger';
  export default {
    name: 'AIInspectDashboard',
    components: {
      InsightsFilters,
      AddInspectToPlan,
      InspectReportPopup,
      ViewersSection,
      
    },
    data() {
      return {
        coatingAnalysisHeaders,
        toolbarHeight: 40,
        timerId: null,
        processingCount: 0,
        accepting: [],
        rejecting: [],
        loading: false,
        entries:[],
        total:0,
        expanded: [],
        reportLoadingId: null,
        statuses: ['Open', 'In Review', 'Verified', 'Closed'],
        anomalyInsightHeaders: [
          {
            text: '',
            value: 'thumbnailUrl',
            sortable: false,
            width: '60px',
          },
          {
            text: 'Anomaly No.',
            value: 'friendlyId',
            sortable: false,
            width: '100px',
          } 
          
        ],
      };
    },
    methods: {
      getFilterOptionDisplayName,
      $truncateString,
      getData() {
        this.loading = true;
        var ops=structuredClone(this.options);
        ops.sortBy = ops.sortBy?.length ? ops.sortBy[0] : null;
        coatingAnalyzerService.get(ops).then((res) => {
          this.entries = res.data.items;
          this.total = res.data.total;
        }).finally(() => {
          this.loading = false;
        });
      },
      getDataDebounced() {
        if (this.timerId == null) {
          this.timerId = -1;
          this.getData();
          return;
        }
        // cancel pending call
        clearTimeout(this.timerId);

        // delay new call 400ms
        this.timerId = setTimeout(() => {
          this.getData();
        }, 400);
      },
      getTypeColor(type) {
        switch (type) {
          case 'Testing':
            return '#00a86b';
          case 'Evaluation':
            return '#f8b400';
          case 'Fabrication':
            return '#f8b400';
          default:
            return '#00a86b';
        }
      },
      getTypeBackground(type) {
        switch (type) {
          case 'Testing':
            return '#00a86b24';
          case 'Evaluation':
            return '#f8b40024';
          case 'Fabrication':
            return '#f8b40024';
          default:
            return '#00a86b24';
        }
      },
      getSeverityColor(severity) {
        switch (`${severity}`) {
          case '1':
            return '#215F9B';
          case '2':
            return '#D2A000';
          case '3':
            return '#C04F15';
          case '4':
            return '#A80000';
          default:
            return '#03624c';
        }
      },
      getSeverityBackground(severity) {
        switch (`${severity}`) {
          case '1':
            return '#215F9B24';
          case '2':
            return '#D2A00024';
          case '3':
            return '#C04F1524';
          case '4':
            return '#A8000024';
          default:
            return '#03624c24';
        }
      },
      getStatusColor(status) {
        const colors = {
          Open: 'orange',
          'In Review': 'blue',
          Verified: 'purple',
          Closed: 'green',
        };
        return colors[status] || 'grey';
      },
      getStatusBackground(status) {
        const backgrounds = {
          Open: '#fb8c0024',
          'In Review': '#2196f324',
          Verified: '#9c27b024',
          Closed: '#4caf5024',
        };
        return backgrounds[status] || '#e0e0e024';
      },
      getPriorityColor(priority) {
        switch (priority?.toLowerCase()) {
          case 'low':
            return '#4CAF50';
          case 'medium':
            return '#FF9800';
          case 'high':
            return '#F44336';
          case 'critical':
            return '#D32F2F';
          default:
            return '#757575';
        }
      },
         canIsolate() {
        let inspects = Object.values(this.$refs.inspectsTable?.selection ?? {});
        return (
          !!inspects.length &&
          inspects?.every((o) => o.masterTagName && o.inspection?.children.length)
        );
      },
         canDeleteMulti() {
        let inspects = Object.values(this.$refs.inspectsTable?.selection ?? {});
        return !!inspects.length && inspects.length > 1;
      },
       async deleteMulti() {
        var inspects = Object.values(this.$refs.inspectsTable.selection);
        this.$dialog.error({
          text: 'Are you sure you want to delete these inspections?',
          title: 'Delete Inspections',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                let ids = inspects.map((o) => o.id);
                this.$store.dispatch('AI_INSPECT/DELETE_MULTI', ids);
              },
            },
          },
        });
      },
      updateItem(item) {
        coatingAnalyzerService.update(item).then(() => {
          this.entries;
        });
      },
      updateSeverity(item, severity) {
        item.severity = severity;
        this.updateItem(item);
      },
      updateStatus(item, status) {
        item.status = status;
        this.updateItem(item);
      },
      async showInspectsPointsOn360(inspect) {
        this.viewerOpened = true;
        aIInspectService.getById(inspect.id).then(res=>{
          var anomalyInsight=res.data;
          this.isolatedInspects = [anomalyInsight];

          if (this.viewerTab == '360 View') {
            this.onViewer360Selected();
          } else {
            this.viewerTab = '360 View';
          }
        })
      },
      async showCoatingAnalysisOn360(coatingAnalysis) {
        // Open the viewer
        this.viewerOpened = true;

        // Get all anomaly insights with maskPoints
        const anomalyInsights = coatingAnalysis.anomalyInsights || [];
        
        if (anomalyInsights.length === 0) {
          this.$dialog.notify.warning('No anomaly insights found for this coating analysis');
          return;
        }

        // Set isolated inspects with mask points data
        this.isolatedInspects = anomalyInsights.map(insight => ({
          ...insight,
          useMaskPoints: true, // Flag to indicate we should use mask points instead of bounding box
        }));

        // Switch to 360 view
        if (this.viewerTab === '360 View') {
          this.onViewer360Selected();
        } else {
          this.viewerTab = '360 View';
        }
      },
      closeInspectViewer() {
        // this.$store.dispatch('AI_INSPECT/CLOSE_INSPECT_VIEWER');
        this.viewerOpened = false;
        this.isolatedInspects = [];
      },
      rejectInspection(inspect) {
        this.$dialog.error({
          text: 'Are you sure you want to reject this inspection?',
          title: 'Delete Level',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('AI_INSPECT/DELETE', inspect.id);
              },
            },
          },
        });
      },
      deleteInspect(inspect) {
        this.$dialog.error({
          text: 'Are you sure you want to delete this inspection?',
          title: 'Delete Level',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('AI_INSPECT/DELETE', inspect.id);
              },
            },
          },
        });
      },
    
      openFilters() {
        this.$refs.filters.open();
      },
      openInspectReport(inspect) {

        // Report already generated
        if (inspect.reportUrl) {
          this.$refs.inspectReport.openDialog({ url: inspect.reportUrl });
          return;
        }

        // Report is being generated
        this.reportLoadingId = inspect.id;
        this.$store.dispatch('AI_INSPECT/GENERATE_REPORT', { id: inspect.id }).then((insight) => {
          this.reportLoadingId = null;
          inspect.reportUrl = insight.reportUrl;
          this.$refs.inspectReport.openDialog({ url: insight.reportUrl });
        }).catch((error) => {
          this.reportLoadingId = null;
          error('Error generating report:', error);
          this.$dialog.notify.error('Error generating report');
        });
      },
      isolateIn3D(inspect) {
        this.viewerOpened = true;
        this.isolatedInspects = [inspect];
        this.viewerTab = '3D View';
        this.$refs.viewersSection.load3dViewer().then(() => {
          this.onViewer3dSelected();
        });
      },
      updateFilters(category, option) {
        let filters = this.options.filters;

        if (!filters[category]) {
          filters[category] = [];
        }

        if (filters[category]?.includes(option)) {
          filters[category] = filters[category].filter((opt) => opt !== option);
        } else {
          filters[category].push(option);
        }

        this.options = {
          ...this.options,
          filters: {
            ...filters,
          },
        };
      },
      async handleIsolateSingleUnmatchedInspect2d(inspect) {
        this.$refs.viewersSection.viewerState.isLoading = true;

        let point3d = null;

        await this.$refs.viewersSection.load3dViewer();
        let image = this.images.find((o) => o.id == inspect?.imageId);
        this.viewer?.isolate();
        var centerYawPitch = pixelToYawPitch(
          inspect.imageXCenterCoordinate,
          inspect.imageYCenterCoordinate,
          image.width ?? 4057,
          image.height ?? 2028,
        );

        let imagePt = image.getCameraPositionInViewer();

        let target = this.forgeService.imageYawPitchToPoint3D(
          image,
          centerYawPitch.yaw,
          centerYawPitch.pitch,
        );

        const ray = new THREE.Ray();

        let direction = target.clone().sub(imagePt.clone());

        ray.set(imagePt, direction);

        let intersections = [];

        let modelIntersection = this.viewer?.impl?.rayIntersect(
          ray,
          false,
          null,
          null,
          intersections,
        );

        if (modelIntersection?.point) {
          point3d = modelIntersection.point;
        }

        let level = this.forgeService.getLevelContainsPoint(point3d) ?? this.$store.state.levels[0];

        if (!level) {
          error('Level not found', point3d);
          this.$dialog.notify.error('Level not found');
          this.$refs.viewersSection.viewerState.isLoading = false;
          return;
        }

        this.$store.state.selectedLevel = level;

        if (!level?.planUrn) {
          error('Plan URN not found', level);
          this.$dialog.notify.error('Plan URN not found');
          this.$refs.viewersSection.viewerState.isLoading = false;
          return;
        }

        this.$refs.viewersSection.load2dViewer(level.planUrn).then(() => {
          this.forgeService.zoomToPoint2D(point3d);
          if (this.isolatedInspects?.inspection?.point) {
            let inspection = Object.assign(new ForgeInspection(), this.isolatedInspects.inspection);
            this.forgeService.getLabels2DExtension().createHighlightingTags([inspection]);
            this.$refs.viewersSection.viewerState.isLoading = false;
          }
        });
      },
      async handleIsolateMatchedInspects2d(inspects) {
        let points = inspects.map((o) => o.inspection?.point);

        let levelsSet = new Set(points.map((o) => this.forgeService.getLevelContainsPoint(o)));

        let levels = Array.from(levelsSet).filter((o) => o);

        let level = levels.length == 1 ? levels[0] : this.$store.state.levels[0];

        if (levels.length > 1) {
          error('Multiple levels found', levels);
          this.$dialog.notify.warning('Multiple levels found');
        }

        this.$store.state.selectedLevel = level;

        if (!level?.planUrn) {
          error('Plan URN not found', level);
          this.$dialog.notify.error('Plan URN not found');
          this.$refs.viewersSection.viewerState.isLoading = false;
          return;
        }

        this.$refs.viewersSection.load2dViewer(level.planUrn).then(() => {
          this.forgeService.zoomToPoints2D(points);
          this.forgeService
            .getLabels2DExtension()
            .createHighlightingTags(inspects.map((o) => o.inspection));
          this.$refs.viewersSection.viewerState.isLoading = false;
        });
      },
      async handleIsolateInspect2d(inspects) {
        let useInspection = inspects.every((o) => !!o.inspection?.point);

        let points = [];

        if (useInspection) {
          points = inspects.map((o) => o.inspection.point);          
        } else {
          await this.$refs.viewersSection.load3dViewer();
          points = inspects.map((o) => this.forgeService.view3dToWorld(o.point3d));
        }

        let levelsSet = new Set(points.map((o) => this.forgeService.getLevelContainsPoint(o)));

        let levels = Array.from(levelsSet).filter((o) => o);

        let level = levels.length == 1 ? levels[0] : this.$store.state.levels[0];

        if (levels.length > 1) {
          error('Multiple levels found', levels);
          this.$dialog.notify.warning('Multiple levels found');
        }

        this.$store.state.selectedLevel = level;

        if (!level?.planUrn) {
          error('Plan URN not found', level);
          this.$dialog.notify.error('Plan URN not found');
          this.$refs.viewersSection.viewerState.isLoading = false;
          return;
        }

        this.$refs.viewersSection.load2dViewer(level.planUrn).then((isNew) => {
          this.forgeService.zoomToPoints2D(points);

          let inspections = [];

          if (useInspection) {
            inspections = inspects.map((o) => Object.assign(new ForgeInspection(), o.inspection));
          } else {
            inspections = inspects.map((o) => {
              let inspection = new ForgeInspection();
              inspection.point = this.forgeService.view3dToWorld(o.point3d);
              return inspection;
            });
          }

          this.$refs.viewersSection.viewerState.isLoading = false;

          if (isNew) {
            setTimeout(() => {
              this.forgeService.getLabels2DExtension().createHighlightingTags(inspections);
            }, 1500);
          } else {
            this.forgeService.getLabels2DExtension().createHighlightingTags(inspections);
          }
        });
      },
      async onViewer2dSelected() {
        if (!this.isolatedInspects?.length) return;

        this.handleIsolateInspect2d(this.isolatedInspects);
      },
      handleIsolateSingleUnmatchedInspect(inspect) {
        if (!inspect) {
          error('Invalid inspect data');
          return null;
        }

        if (inspect.dbIds?.length) {
          this.viewer?.isolate();
          this.forgeService.getMainExtension()?.isolateDbIds(inspect.dbIds);
          return;
        }

        const image = this.images.find((o) => o.id === inspect.imageId);
        if (!image) {
          error('Image not found', inspect.imageId);
          return null;
        }

        this.viewer?.isolate();

        const imagePt = image.getCameraPositionInViewer();

        const tags = getTagsFrom3DPoint(this.viewer, inspect.point3d, imagePt);

        if (tags.length) {
          let dbIds = tags.flatMap((p) => p.children.map((c) => c.dbId));
          this.forgeService.getMainExtension()?.isolateDbIds(dbIds);
        } else {
          this.forgeService.zoomToPoint3dViewer(inspect.point3d);
          this.$dialog.notify.warning('No tag found for the given 3D point');
          error('No tags found for the given 3D point');
        }
      },
      handleIsolateMatchedInspects(inspects) {
        let inspections = inspects.map((i) => Object.assign(new ForgeInspection(), i.inspection));
        inspections.forEach((i) => i.parseContent());

        let dbIds = inspects.flatMap((o) => o.inspection?.children).map((o) => o.dbId);

        this.viewer?.isolate();
        this.forgeService.getMainExtension()?.isolateDbIds(dbIds);
        this.forgeService.getLabels3DExtension().createHighlightingTags(inspections);
      },
      onViewer3dSelected() {
        if (!this.isolatedInspects?.length) return;

        if (
          this.isolatedInspects.length == 1 &&
          !this.isolatedInspects[0].inspection?.children.length
        ) {
          this.handleIsolateSingleUnmatchedInspect(this.isolatedInspects[0]);
        } else {
          this.handleIsolateMatchedInspects(this.isolatedInspects);
        }
      },
      onViewer360Selected() {
        if (!this.isolatedInspects?.length) return;
        
        // Check if using Photo Sphere Viewer
        const usePhotoSphereViewer = this.$refs.viewersSection?.usePhotoSphereViewer;
        
        if (usePhotoSphereViewer) {
          // Photo Sphere Viewer handles display via watcher in ViewersSection
          // No need to dispatch to Pannellum viewer
          return;
        }
        
        // Legacy Pannellum viewer
        this.$store.dispatch('AI_INSPECT/SHOW_INSPECTS_IN_VIEWER', this.isolatedInspects[0]);
      },
      navigateToInspect(inspect) {
        this.$store.dispatch('AI_INSPECT/SHOW_INSPECTS_IN_VIEWER', inspect);
      },
      handleViewerTabChange() {
        if (this.viewerTab === '3D View') {
          this.$refs.viewersSection.load3dViewer().then(() => {
            this.onViewer3dSelected();
          });
        } else if (this.viewerTab === '2D View') {
          this.onViewer2dSelected();
        } else if (this.viewerTab === '360 View') {
          this.onViewer360Selected();
        }
      },
      openViewer() {
        this.viewerOpened = true;
        this.handleViewerTabChange();
      },
    },
    computed: {
      ...mapState('AI_INSPECT', ['images']),
      ...mapGetters('AI_INSPECT', {
        openedInspect: 'CURRENT_INSPECT',
      }),
      aIInspectHeaders() {
        return this.coatingAnalysisHeaders;
      },
      isolatedInspects: {
        get() {
          return this.$store.getters['AI_INSPECT/ISOLATED_INSPECTS'];
        },
        set(value) {
          value = value.map((o) => {
            o.inspection = Object.assign(new ForgeInspection(), o.inspection);
            o.inspection.parseContent();
            return o;
          });

          this.$store.commit('AI_INSPECT/SET_ISOLATED_INSPECTS', value);
        },
      },
      currentInspect: {
        get() {
          return this.isolatedInspects.length === 1 ? this.isolatedInspects[0] : null;
        },
      },
      availableScenes: {
        get() {
          if (!this.currentInspect?.children?.length) return [];
          return [this.currentInspect, ...this.currentInspect.children];
        },
      },
      viewerOpened: {
        get() {
          return this.$store.getters['AI_INSPECT/VIEWER_OPENED'];
        },
        set(val) {
          this.$store.commit('AI_INSPECT/SET_VIEWER_OPENED', val);
        },
      },
      viewerTab: {
        get() {
          return this.$store.getters['AI_INSPECT/VIEWER_TAB'];
        },
        set(val) {
          this.$store.commit('AI_INSPECT/SET_VIEWER_TAB', val);
        },
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      pageView3d() {
        return this.$store.getters['GET_PAGE_VIEW_3D'];
      },
      viewer() {
        return this.$store.state.viewer;
      },
      images: {
        get() {
          return this.$store.getters['AI_INSPECT/IMAGES'];
        },
      },
      facility: {
        get() {
          return this.$store.getters['FACILITY/GET_FACILITY'];
        },
      },
      inspectViewer: {
        get() {
          return this.$store.getters['AI_INSPECT/INSPECT_VIEWER'];
        },
        set(val) {
          this.$store.commit('AI_INSPECT/SET_INSPECT_VIEWER', val);
        },
      },
      options: {
        get() {
          return this.$store.getters['AI_INSPECT/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('AI_INSPECT/SET_OPTIONS', val);
        },
      },
      filters: {
        get() {
          return this.options.filters;
        },
      },
      filtersApplied() {
        return Object.values(this.filters).flat().length > 0;
      },
    },
    mounted() {
      this.$store.dispatch('AI_INSPECT/GET_IMAGES');
    },
    created() {
      this.options.facilityId = this.$route.params.id;
     this.getData()
    },
    activated() {
      this.handleViewerTabChange();
      this.getDataDebounced();
    },
    watch: {
      options: {
        handler: function () {
          this.getDataDebounced();
        },
        deep: true,
      },
      viewerTab: {
        handler: function () {
          if (!this.viewerOpened) return;

          this.handleViewerTabChange();
        },
      },
    },
  };
</script>
<style lang="scss">
  #ai-inspect-wrapper {
    height: calc(100vh - 160px);

    &.filter-applied {
      height: calc(100vh - 200px);
    }

    .search-input {
      & .v-input__slot {
        padding: 0 16px;
      }
    }

    .viewers-section {
      display: flex;
      flex-direction: column;
    }

    .table-sticky {
      overflow: hidden;
      height: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      border-radius: 8px;

      .v-table__wrapper {
        overflow: overlay;
        height: 100%;
        flex: 1 1 auto;
        padding-bottom: 0;
      }

      .v-data-table-header {
        position: sticky;
        z-index: 6;
        top: 0;
        background: var(--v-light-grey-base) !important;
      }

      .v-data-footer {
        position: relative !important;
        background: var(--v-light-grey-base) !important;
        border-radius: 0px 0px 8px 8px;
      }
    }

    .inspect-thumbnail {
      padding: 4px;

      .thumbnail-wrapper {
        border-radius: 4px;
        background: #dcdcdc;

        & img {
          border-radius: 8px;
        }
      }
    }

    .inspect-canvas:hover {
      // cursor: pointer;
    }

    .v-data-table__selected {
      background: #03624c24;
    }

    .anomaly-insights-table tbody tr {
      cursor: pointer;
    }

    .pnlm-controls-container {
      display: block !important;
    }
  }

  .theme--light.v-data-table tbody tr:nth-of-type(even) {
    /* background-color: rgba(0, 0, 0, 0.02); */
  }

  .theme--dark.v-data-table tbody tr:nth-of-type(even) {
    /* background-color: rgba(0, 0, 0, 0.5); */
  }

  .v-tooltip__content.menuable__content__active {
    opacity: 1;
  }

  .scroller {
    overflow-x: auto;
    white-space: nowrap;
  }

  .item-chip{
    margin: 0px !important;
    padding: 0px !important;
    font-size: 13px;
    min-height: 24px;
  }
</style>
